/**
 * Hubspot Dropdown Utilities
 * Replaces native Hubspot select elements with custom Bootstrap dropdowns
 */

export const replaceSelectWithCustomDropdown = (selectElement) => {
	if (!selectElement || selectElement.dataset.customDropdownApplied) return;

	// Mark as processed to avoid duplicate replacement
	selectElement.dataset.customDropdownApplied = 'true';

	// Hide the original select
	selectElement.style.display = 'none';

	// Create custom dropdown wrapper
	const dropdownWrapper = document.createElement('div');
	dropdownWrapper.className = 'dropdown w-100';

	// Get selected option text or placeholder
	const selectedOption = selectElement.options[selectElement.selectedIndex];
	const selectedText =
		selectedOption && selectedOption.value
			? selectedOption.text
			: selectElement.querySelector('option[value=""]')?.text ||
				'Select...';

	// Create dropdown button
	const button = document.createElement('button');
	button.className =
		'dropdown-toggle background-color-white font-size-sm font-weight-regular';
	button.type = 'button';
	button.setAttribute('data-bs-toggle', 'dropdown');
	button.setAttribute('aria-expanded', 'false');
	button.innerHTML = `<span class="dropdown-selected-text">${selectedText}</span>`;

	// Create dropdown menu
	const menu = document.createElement('ul');
	menu.className = 'dropdown-menu w-100 background-color-white';

	// Populate dropdown items from select options
	Array.from(selectElement.options).forEach((option) => {
		// Skip empty placeholder options
		if (!option.value && option.disabled) return;

		const li = document.createElement('li');
		const a = document.createElement('a');
		a.className =
			'dropdown-item text-color-deep p-3 px-4 font-size-sm mb-0 font-weight-medium';
		a.href = '#';
		a.textContent = option.text;
		a.dataset.value = option.value;

		// Mark currently selected item
		if (option.selected && option.value) {
			a.classList.add('active');
		}

		// Handle dropdown item click
		a.addEventListener('click', (e) => {
			e.preventDefault();

			// Update the hidden select element
			selectElement.value = a.dataset.value;

			// Trigger change event on select for form validation
			const changeEvent = new Event('change', { bubbles: true });
			selectElement.dispatchEvent(changeEvent);

			// Update button text
			button.querySelector('.dropdown-selected-text').textContent =
				a.textContent;

			// Update active state
			menu.querySelectorAll('.dropdown-item').forEach((item) =>
				item.classList.remove('active'),
			);
			a.classList.add('active');
		});

		li.appendChild(a);
		menu.appendChild(li);
	});

	// Assemble dropdown
	dropdownWrapper.appendChild(button);
	dropdownWrapper.appendChild(menu);

	// Insert custom dropdown after the original select
	selectElement.parentNode.insertBefore(
		dropdownWrapper,
		selectElement.nextSibling,
	);
};

/**
 * Apply custom dropdowns to all select elements in a form
 */
export const applyCustomDropdownsToForm = (form) => {
	if (!form) return;

	// Find all select elements in the form
	const selects = form.querySelectorAll('select');

	selects.forEach((select) => {
		// Apply custom dropdown to each select
		replaceSelectWithCustomDropdown(select);
	});
};

/**
 * Apply custom dropdowns to all select elements in a container
 */
export const applyCustomDropdownsToContainer = (
	container = document,
	selector = 'select',
) => {
	if (!container) return;

	// Find all select elements in the container
	const selects = container.querySelectorAll(selector);

	selects.forEach((select) => {
		// Apply custom dropdown to each select
		replaceSelectWithCustomDropdown(select);
	});
};

/**
 * Find fields by name and add field name as class to parent row
 * @param {HTMLElement|Document} container - Container to search within
 */
export const addFieldNameClassToRows = (container = document) => {
	if (!container) return;

	const formContent =
		container.querySelector('.hsfc-Step__Content') || container;

	const fieldSelectors = ['input[name]', 'select[name]', 'textarea[name]'];

	fieldSelectors.forEach((selector) => {
		const fields = formContent.querySelectorAll(selector);

		fields.forEach((field) => {
			const name = field.getAttribute('name');
			if (!name || !name.includes('/')) return;

			const fieldName = name.split('/').pop();
			if (!fieldName) return;

			const row = field.closest('.hsfc-Row');
			if (row && !row.classList.contains(`field-${fieldName}`)) {
				row.classList.add(`field-${fieldName}`);
			}
		});
	});
};
